/**
 * GameDetailView - Displays and allows editing of game details
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GameStatus } from '../models/GameEntry.js';
import { LoadingSpinner, ErrorMessage, Button } from '@code-burrow/design-system';
import { useUserView, projectUserFields } from '@code-burrow/tracker-core';

function GameDetailView({ backendService }) {
    const { steamID } = useParams();
    const navigate = useNavigate();
    const { activeView } = useUserView();
    const [game, setGame] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        status: '',
        rating: ''
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadGame();
    }, [steamID, activeView]);

    const loadGame = async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await backendService.getGame(steamID);

            if (!result.success) {
                setError(result.error);
                return;
            }

            if (!result.entry) {
                setError('Game not found');
                return;
            }

            const projected = projectUserFields(result.entry, 'videogames', activeView);
            setGame(projected);
            setFormData({
                status: projected.status || '',
                rating: projected.rating || ''
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);

        const updates = {
            status: formData.status || null,
            rating: formData.rating ? parseInt(formData.rating) : null
        };

        try {
            await backendService.upsertUserGame(activeView, game.id, updates);
            await loadGame();
            setEditing(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            status: game.status || '',
            rating: game.rating || ''
        });
        setEditing(false);
    };

    const renderStars = (rating) => {
        if (!rating) return '-';
        return '★'.repeat(rating) + '☆'.repeat(5 - rating);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <LoadingSpinner size="large" message="Loading game details..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4">
                <ErrorMessage message={error} type="api" />
                <Button onClick={() => navigate('/')} variant="secondary" className="mt-4">
                    Back to Collection
                </Button>
            </div>
        );
    }

    if (!game) {
        return null;
    }

    return (
        <div className="bg-bg-primary min-h-screen py-8">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                {/* Back button */}
                <Button
                    onClick={() => navigate('/table')}
                    variant="secondary"
                    size="sm"
                    className="mb-6"
                    icon={
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    }
                >
                    Back to Table
                </Button>

                {/* Game header */}
                <div className="bg-surface rounded-lg shadow-sm border border-border-subtle overflow-hidden mb-6">
                    <div className="md:flex">
                        <div className="md:flex-shrink-0">
                            <img
                                src={game.imageURL}
                                alt={game.title}
                                className="h-48 w-full object-cover md:w-64"
                            />
                        </div>
                        <div className="p-8">
                            <h1 className="text-3xl font-bold text-text-primary mb-2">
                                {game.title}
                            </h1>
                            <p className="text-lg text-text-secondary mb-4">
                                {game.creator}
                            </p>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {game.genres?.map(genre => (
                                    <span
                                        key={genre}
                                        className="inline-flex items-center rounded-full bg-accent-secondary/10 px-3 py-1 text-sm font-medium text-accent-primary"
                                    >
                                        {genre}
                                    </span>
                                ))}
                            </div>
                            <a
                                href={game.storeURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-accent-primary hover:text-accent-hover"
                            >
                                {game.source === 'rawg' ? 'View on RAWG' : 'View on Steam'}
                                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Game info */}
                <div className="bg-surface rounded-lg shadow-sm border border-border-subtle p-6 mb-6">
                    <h2 className="text-xl font-semibold text-text-primary mb-4">Game Information</h2>
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <dt className="text-sm font-medium text-text-secondary">Release Date</dt>
                            <dd className="mt-1 text-sm text-text-primary">{formatDate(game.releaseDate)}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-text-secondary">Game ID</dt>
                            <dd className="mt-1 text-sm text-text-primary">{game.steamID}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-text-secondary">Type</dt>
                            <dd className="mt-1 text-sm text-text-primary">{game.isDLC ? 'DLC' : 'Game'}</dd>
                        </div>
                    </dl>
                </div>

                {/* User tracking data */}
                <div className="bg-surface rounded-lg shadow-sm border border-border-subtle p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-text-primary">Your Tracking Data</h2>
                        {!editing && (
                            <Button
                                onClick={() => setEditing(true)}
                                variant="secondary"
                                size="sm"
                                icon={
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                }
                            >
                                Edit
                            </Button>
                        )}
                    </div>

                    {editing ? (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-text-primary mb-1">
                                    Status
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="block w-full rounded-md border border-border bg-bg-secondary text-text-primary px-3 py-2 shadow-sm focus:border-accent-primary focus:ring-1 focus:ring-accent-primary sm:text-sm"
                                >
                                    <option value="">Not set</option>
                                    {Object.values(GameStatus).map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-primary mb-1">
                                    Rating
                                </label>
                                <select
                                    value={formData.rating}
                                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                                    className="block w-full rounded-md border border-border bg-bg-secondary text-text-primary px-3 py-2 shadow-sm focus:border-accent-primary focus:ring-1 focus:ring-accent-primary sm:text-sm"
                                >
                                    <option value="">Not rated</option>
                                    <option value="1">★☆☆☆☆ (1)</option>
                                    <option value="2">★★☆☆☆ (2)</option>
                                    <option value="3">★★★☆☆ (3)</option>
                                    <option value="4">★★★★☆ (4)</option>
                                    <option value="5">★★★★★ (5)</option>
                                </select>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    onClick={handleSave}
                                    variant="primary"
                                    loading={saving}
                                    disabled={saving}
                                >
                                    Save Changes
                                </Button>
                                <Button
                                    onClick={handleCancel}
                                    variant="secondary"
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <dt className="text-sm font-medium text-text-secondary">Status</dt>
                                <dd className="mt-1 text-sm text-text-primary">{game.status || '-'}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-text-secondary">Rating</dt>
                                <dd className="mt-1 text-sm text-accent-primary">{renderStars(game.rating)}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-text-secondary">Last Played</dt>
                                <dd className="mt-1 text-sm text-text-primary">{formatDate(game.lastPlayed)}</dd>
                            </div>
                        </dl>
                    )}
                </div>
            </div>
        </div>
    );
}

export default GameDetailView;
